"use client"
import { useState, useEffect, useRef, useMemo } from "react"
import {
  Search,
  UploadIcon,
  Book,
  Loader2,
  Settings,
  X,
  Upload,
  FileText,
  AlertCircle,
  Edit,
  Trash2,
  ChevronUp,
  ChevronDown,
  Trash,
  Copy,
  Plus,
  Filter,
} from "lucide-react"
import AddQuestionQuestionbankEditExtractedQuetsions from "./AddQuestionQuestionbankEditExtractedQuetsions"
import AddQuestionAddExamType from "./AddQuestionAddExamType"
import { postAws } from "../postAws"
import { postTeacherUploadFileFOrQuestionBank } from "../postFileAws"
import AddQuestionUploadAnswerkey from "./AddQuestionUploadAnswerkey"

const API_URL = "https://api-dev.aurait.ai/QuestionBank"
const MAX_PAGES = 150
const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null)

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value)
    const content = editorRef.current?.innerHTML || ""
    onChange(content)
  }

  const handleInput = (e) => {
    onChange(e.target.innerHTML)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData("text/plain")
    document.execCommand("insertText", false, text)
    onChange(e.target.innerHTML)
  }

  useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value
    }
  }, [value])

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => formatText("bold")}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => formatText("italic")}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => formatText("underline")}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => formatText("insertUnorderedList")}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => formatText("insertOrderedList")}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          1. List
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="p-4 min-h-[120px] focus:outline-none"
        style={{
          direction: "ltr",
          textAlign: "left",
          unicodeBidi: "normal",
        }}
        placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  )
}

// Updated question type options to match earlier version with user-friendly names
const questionTypeOptions = [
  "Single Correct Option",
  "Multiple Correct",
  "Fill in the Blanks",
  "True/False",
  "Integer Type",
  "Comprehension",
  "Assertion-Reasoning",
  "Matrix-Match",
  "Paragraph",
]

// Added default instructions for each question type to match earlier version
const defaultQuestionTypeInstructions = {
  "Single Correct Option":
    "Multiple Choice Questions with 4 options where only 1 option is correct. Students must select the best answer from the given choices.",
  "Multiple Correct":
    "Multiple Choice Questions with 4 options where multiple options can be correct. Students must select all correct answers.",
  "Fill in the Blanks":
    "Questions where students need to fill in missing words or phrases in the given sentences or statements.",
  "True/False": "Statements that students must evaluate as either True or False based on their knowledge.",
  Numeric: "Questions requiring numerical answers. Students must enter the exact numerical value as the answer.",
  Comprehension:
    "Reading comprehension questions based on a given passage. Students must read and understand the passage to answer related questions.",
  "Assertion-Reasoning":
    "Questions with two statements - Assertion (A) and Reason (R). Students must evaluate the truth of both statements and their relationship.",
  "Matrix-Match":
    "Questions where students need to match items from two columns or matrices based on their relationships.",
  Paragraph: "Descriptive questions requiring detailed written answers in paragraph form.",
}

const AddQuestionQuestionBankUploadAndEditor = () => {
  const [books, setBooks] = useState([])
  const [filteredBooks, setFilteredBooks] = useState([])
  const [searchTerm, setSearchTerm] = useState("")

  const [filters, setFilters] = useState({
    examType: "",
    questionType: "",
    chapter: "",
  })
  const [showFilters, setShowFilters] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showExamTypeModal, setShowExamTypeModal] = useState(false)
  const [showViewExamTypesModal, setShowViewExamTypesModal] = useState(false)
  const [showEditExamTypeModal, setShowEditExamTypeModal] = useState(false)
  const [selectedExamTypeForEdit, setSelectedExamTypeForEdit] = useState(null)
  const [editExamTypeForm, setEditExamTypeForm] = useState({
    examName: "",
    examInstructions: "",
    subjects: [],
  })
  const [editLoading, setEditLoading] = useState(false)
  const [examTypes, setExamTypes] = useState([])
  const [availableSubjects, setAvailableSubjects] = useState([])
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState([])
  const [examTypesLoading, setExamTypesLoading] = useState(false)
  const [questionTypesLoading, setQuestionTypesLoading] = useState(false)
  const [uploadForm, setUploadForm] = useState({
    book: "",
    class: "",
    subject: "",
    chapter: "",
    examType: "",
    questionType: "",
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [filePageCount, setFilePageCount] = useState(null)
  const [pageCountLoading, setPageCountLoading] = useState(false)
  const [pageCountError, setPageCountError] = useState(null)
  const [createQuestionsLoading, setCreateQuestionsLoading] = useState(false)
  const [selectedBookId, setSelectedBookId] = useState(null)
  const [showBookDetailsModal, setShowBookDetailsModal] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [bookToDelete, setBookToDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [questionTypeInstructions, setQuestionTypeInstructions] = useState({})
  const [expandedQuestionTypes, setExpandedQuestionTypes] = useState({})

  const [editQuestionTypeInstructions, setEditQuestionTypeInstructions] = useState({})
  const [expandedEditQuestionTypes, setExpandedEditQuestionTypes] = useState({})
  const [showUploadAnswerKeyModal, setShowUploadAnswerKeyModal] = useState(false)
  const [pendingQTKey, setPendingQTKey] = useState(null)

  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copyTargetBook, setCopyTargetBook] = useState(null)
  const [copyExamType, setCopyExamType] = useState("")
  const [copyLoading, setCopyLoading] = useState(false)
  const [initialImageUrisForEdit, setInitialImageUrisForEdit] = useState([])
  const [selectedBookImageUris, setSelectedBookImageUris] = useState([])
  const duplicateLabelMap = useMemo(() => {
    const groups = new Map()
    ;(books || []).forEach((b) => {
      const key =
        `${String(b.class || "").toLowerCase()}|` +
        `${String(b.subject || "").toLowerCase()}|` +
        `${String(b.chapter || "").toLowerCase()}|` +
        `${String(b.examType || "").toLowerCase()}|` +
        `${String(b.questionType || b.originalQuestionType || "").toLowerCase()}`

      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(b)
    })

    const map = {}
    groups.forEach((arr) => {
      // Oldest first
      arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      arr.forEach((book, idx) => {
        // Oldest = 0 (no badge), next = 1, newest (if 3) = 2 ...
        map[book._id] = { label: idx === 0 ? 0 : idx, count: arr.length }
      })
    })
    return map
  }, [books])
  const fetchExamTypes = async () => {
    try {
      setExamTypesLoading(true)
      const response = await postAws(API_URL, "POST", {
        request: "get-exam-types",
      })

      if (response && response.body && response.body.data) {
        setExamTypes(response.body.data)
      }
    } catch (error) {
      console.error("Error fetching exam types:", error)
    } finally {
      setExamTypesLoading(false)
    }
  }

  const fetchQuestionTypeInstructions = async (examType, subject, questionType) => {
    try {
      const response = await postAws(API_URL, "POST", {
        request: "get-question-type-instructions",
        body: { examType, subject, questionType },
      })

      if (response && response.body && response.body.data) {
        const key = `${examType}-${subject}-${questionType}`
        setQuestionTypeInstructions((prev) => ({
          ...prev,
          [key]: response.body.data.instructions || defaultQuestionTypeInstructions[questionType] || "",
        }))
        // Auto-expand if instructions exist
        if (response.body.data.instructions || defaultQuestionTypeInstructions[questionType]) {
          setExpandedQuestionTypes((prev) => ({
            ...prev,
            [key]: true,
          }))
        }
      }
    } catch (error) {
      console.error("Error fetching question type instructions:", error)
      // Fallback to default instructions if API fails
      const key = `${examType}-${subject}-${questionType}`
      setQuestionTypeInstructions((prev) => ({
        ...prev,
        [key]: defaultQuestionTypeInstructions[questionType] || "",
      }))
    }
  }

  const saveQuestionTypeInstructions = async (examType, subject, questionType, instructions) => {
    try {
      const response = await postAws(API_URL, "POST", {
        request: "save-question-type-instructions",
        body: {
          examType,
          subject,
          questionType,
          instructions,
          teacherId: localStorage.getItem("teacherId") || "",
          classroomId: localStorage.getItem("classRoomId") || "",
        },
      })

      if (response && response.statusCode === 200) {
        alert("Instructions saved successfully!")
      }
    } catch (error) {
      console.error("Error saving instructions:", error)
      alert("Failed to save instructions. Please try again.")
    }
  }

  const fetchQuestionTypes = async (examType, subject) => {
    if (!examType || !subject) return

    try {
      setQuestionTypesLoading(true)
      const response = await postAws(API_URL, "POST", {
        request: "get-question-types",
        body: { examType, subject },
      })

      if (response && response.body && response.body.data) {
        setAvailableQuestionTypes(response.body.data)

        // Fetch instructions for each question type
        response.body.data.forEach((questionType) => {
          fetchQuestionTypeInstructions(examType, subject, questionType)
        })
      }
    } catch (error) {
      console.error("Error fetching question types:", error)
      setAvailableQuestionTypes([])
    } finally {
      setQuestionTypesLoading(false)
    }
  }

  const fetchAllBooks = async () => {
    try {
      setLoading(true)
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")

      const response = await postAws("https://api-dev.aurait.ai/QuestionBank/invocation", "POST", {
        request: "get-all-books",
        body: {
          teacherId: teacherId || "",
          classroomId: classroomId || "",
        },
      })

      if (response && response.body && response.body.books) {
        setBooks(response.body.books)
        setFilteredBooks(response.body.books)
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books")
    } finally {
      setLoading(false)
    }
  }

  const handleViewExamTypesClick = () => {
    setShowViewExamTypesModal(true)
  }

  const handleCloseViewExamTypesModal = () => {
    setShowViewExamTypesModal(false)
  }

  const handleEditExamType = (examType) => {
    setSelectedExamTypeForEdit(examType)
    setEditExamTypeForm({
      examName: examType.examName,
      examInstructions: examType.examInstructions || "",
      subjects: examType.subjects.map((subject) => ({
        name: subject.name,
        totalQuestions: subject.totalQuestions,
        timeInMinutes: subject.timeInMinutes,
        questionTypes: subject.questionTypes.map((qt) => ({
          type: qt.type,
          count: qt.count,
          instructions: qt.instructions || "",
          // Added marking scheme fields from server response
          marksForCorrect: qt.marksForCorrect,
          marksForIncorrect: qt.marksForIncorrect,
          marksForLeaving: qt.marksForLeaving,
        })),
      })),
    })

    const instructionsState = {}
    const expandedState = {}
    examType.subjects.forEach((subject, subjectIndex) => {
      subject.questionTypes.forEach((qt, qtIndex) => {
        const key = `${subjectIndex}-${qtIndex}`
        instructionsState[key] = qt.instructions || ""
        if (qt.instructions && qt.instructions.trim()) {
          expandedState[key] = true
        }
      })
    })
    setEditQuestionTypeInstructions(instructionsState)
    setExpandedEditQuestionTypes(expandedState)

    setShowEditExamTypeModal(true)
  }

  const handleCloseEditExamTypeModal = () => {
    setShowEditExamTypeModal(false)
    setSelectedExamTypeForEdit(null)
    setEditExamTypeForm({
      examName: "",
      examInstructions: "",
      subjects: [],
    })
  }

  const handleEditFormChange = (field, value) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleEditSubjectChange = (subjectIndex, field, value) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, index) =>
        index === subjectIndex ? { ...subject, [field]: value } : subject,
      ),
    }))
  }

  const handleEditQuestionTypeChange = (subjectIndex, questionTypeIndex, field, value) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, sIndex) =>
        sIndex === subjectIndex
          ? {
              ...subject,
              questionTypes: subject.questionTypes.map((qt, qtIndex) => {
                if (qtIndex === questionTypeIndex) {
                  // Auto-populate default instructions when question type changes
                  if (field === "type" && value && defaultQuestionTypeInstructions[value]) {
                    const key = `${subjectIndex}-${qtIndex}`
                    setEditQuestionTypeInstructions((prev) => ({
                      ...prev,
                      [key]: qt.instructions || defaultQuestionTypeInstructions[value],
                    }))
                    setExpandedEditQuestionTypes((prev) => ({
                      ...prev,
                      [key]: true,
                    }))
                    return {
                      ...qt,
                      [field]: value,
                      instructions: qt.instructions || defaultQuestionTypeInstructions[value],
                    }
                  }
                  return { ...qt, [field]: value }
                }
                return qt
              }),
            }
          : subject,
      ),
    }))
  }

  const addEditSubject = () => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          name: "",
          totalQuestions: 1,
          timeInMinutes: 60,
          questionTypes: [{ type: "MCQ", count: 1 }],
        },
      ],
    }))
  }

  const removeEditSubject = (subjectIndex) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((_, index) => index !== subjectIndex),
    }))
  }

  const addEditQuestionType = (subjectIndex) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, index) =>
        index === subjectIndex
          ? {
              ...subject,
              questionTypes: [...subject.questionTypes, { type: "MCQ", count: 1, instructions: "" }], // Include instructions field for new question types
            }
          : subject,
      ),
    }))
  }

  const removeEditQuestionType = (subjectIndex, questionTypeIndex) => {
    setEditExamTypeForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, sIndex) =>
        sIndex === subjectIndex
          ? {
              ...subject,
              questionTypes: subject.questionTypes.filter((_, qtIndex) => qtIndex !== questionTypeIndex),
            }
          : subject,
      ),
    }))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()

    if (!editExamTypeForm.examName.trim()) {
      alert("Please enter exam name")
      return
    }

    if (editExamTypeForm.subjects.length === 0) {
      alert("Please add at least one subject")
      return
    }

    // Validate subjects
    for (let i = 0; i < editExamTypeForm.subjects.length; i++) {
      const subject = editExamTypeForm.subjects[i]
      if (!subject.name.trim()) {
        alert(`Please enter name for subject ${i + 1}`)
        return
      }
      if (subject.totalQuestions <= 0) {
        alert(`Please enter valid total questions for subject ${subject.name}`)
        return
      }
      if (subject.timeInMinutes <= 0) {
        alert(`Please enter valid time for subject ${subject.name}`)
        return
      }
      if (subject.questionTypes.length === 0) {
        alert(`Please add at least one question type for subject ${subject.name}`)
        return
      }

      // Validate question types
      for (let j = 0; j < subject.questionTypes.length; j++) {
        const qType = editExamTypeForm.subjects[j]
        if (!qType.type) {
          alert(`Please select question type ${j + 1} for subject ${subject.name}`)
          return
        }
        if (qType.count <= 0) {
          alert(`Please enter valid count for question type ${qType.type} in subject ${subject.name}`)
          return
        }
      }

      // Check if question type counts match total questions
      const totalTypeQuestions = subject.questionTypes.reduce((sum, qType) => sum + qType.count, 0)
      if (totalTypeQuestions !== subject.totalQuestions) {
        alert(
          `Total question types count (${totalTypeQuestions}) doesn't match total questions (${subject.totalQuestions}) for subject ${subject.name}`,
        )
        return
      }
    }

    try {
      setEditLoading(true)

      const subjectsWithInstructions = editExamTypeForm.subjects.map((subject, subjectIndex) => ({
        ...subject,
        questionTypes: subject.questionTypes.map((qt, qtIndex) => {
          const key = `${subjectIndex}-${qtIndex}`
          return {
            ...qt,
            instructions: editQuestionTypeInstructions[key] || "",
          }
        }),
      }))

      const response = await postAws(API_URL, "POST", {
        request: "edit-exam-type",
        body: {
          id: selectedExamTypeForEdit._id,
          examName: editExamTypeForm.examName.trim(),
          examInstructions: editExamTypeForm.examInstructions,
          subjects: subjectsWithInstructions, // Send subjects with question type instructions
          teacherId: localStorage.getItem("teacherId") || "",
          classroomId: localStorage.getItem("classRoomId") || "",
        },
      })

      if (response && response.statusCode === 200) {
        alert("Exam type updated successfully!")
        handleCloseEditExamTypeModal()
        fetchExamTypes()
      } else {
        alert("Failed to update exam type. Please try again.")
      }
    } catch (error) {
      console.error("Error updating exam type:", error)
      alert("Failed to update exam type. Please try again.")
    } finally {
      setEditLoading(false)
    }
  }

  const calculateSuccessPercentage = (successfulChunks, totalChunks) => {
    return totalChunks > 0 ? ((successfulChunks / totalChunks) * 100).toFixed(2) : 0
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800"
      case "completed":
        return "bg-green-100 text-green-800"
      case "Processing":
        return "bg-yellow-100 text-yellow-800"
      default:
        return "bg-red-100 text-red-800"
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const calculateDaysSinceCreation = (createdAt) => {
    const createdDate = new Date(createdAt)
    const now = new Date()
    const diffInMs = now.getTime() - createdDate.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    return diffInDays
  }

  const handleDeleteBook = async (bookId) => {
    try {
      setDeleteLoading(true)

      const response = await postAws("https://api-dev.aurait.ai/QuestionBank/invocation", "POST", {
        request: "delete-book-by-id",
        body: {
          id: bookId,
        },
      })

      if (response && response.statusCode === 200) {
        alert("Book deleted successfully!")
        fetchAllBooks() // Refresh the books list
        setShowDeleteConfirmation(false)
        setBookToDelete(null)
      } else {
        alert("Failed to delete book. Please try again.")
      }
    } catch (error) {
      console.error("Delete error:", error)
      alert("Failed to delete book. Please try again.")
    } finally {
      setDeleteLoading(false)
    }
  }

  const showDeleteConfirmationDialog = (book) => {
    setBookToDelete(book)
    setShowDeleteConfirmation(true)
  }

  const cancelDelete = () => {
    setShowDeleteConfirmation(false)
    setBookToDelete(null)
  }

  const handleBookClick = (bookId) => {
    setSelectedBookId(bookId)
    setShowBookDetailsModal(true)
  }

  const handleCloseBookDetailsModal = () => {
    setShowBookDetailsModal(false)
  }

  const handleUploadClick = async () => {
    setShowUploadModal(true)
  }

  const handleExamTypeClick = () => {
    setShowExamTypeModal(true)
  }

  const handleCloseExamTypeModal = () => {
    setShowExamTypeModal(false)
    fetchExamTypes()
  }

  const handleCloseUploadModal = () => {
    setShowUploadModal(false)
    setUploadForm({
      book: "",
      class: "",
      subject: "",
      chapter: "",
      examType: "",
      questionType: "", // Reset single questionType
    })
    setSelectedFile(null)
    setFilePageCount(null)
    setPageCountError(null)
    setAvailableSubjects([])
    setAvailableQuestionTypes([])
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setUploadForm((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (name === "examType") {
      const selectedExam = examTypes.find((exam) => exam.examName === value)
      if (selectedExam) {
        setAvailableSubjects(selectedExam.subjects.map((subject) => subject.name))
      } else {
        setAvailableSubjects([])
      }
      setUploadForm((prev) => ({
        ...prev,
        subject: "",
        questionType: "",
      }))
      setAvailableQuestionTypes([])
    }

    if (name === "subject") {
      setUploadForm((prev) => ({
        ...prev,
        questionType: "",
      }))
      if (value && uploadForm.examType) {
        fetchQuestionTypes(uploadForm.examType, value)
      } else {
        setAvailableQuestionTypes([])
      }
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setPageCountError("File size exceeds 100MB limit")
      return
    }

    if (file.type !== "application/pdf") {
      setPageCountError("Please select a PDF file")
      return
    }

    setSelectedFile(file)
    setPageCountError(null)
    setPageCountLoading(true)

    try {
      const arrayBuffer = await file.arrayBuffer()
      const uint8Array = new Uint8Array(arrayBuffer)

      // Simple PDF page count estimation
      const text = new TextDecoder().decode(uint8Array)
      const pageMatches = text.match(/\/Type\s*\/Page[^s]/g)
      const pageCount = pageMatches ? pageMatches.length : 1

      setFilePageCount(pageCount)

      if (pageCount > MAX_PAGES) {
        setPageCountError(`PDF has ${pageCount} pages. Maximum allowed is ${MAX_PAGES} pages.`)
      }
    } catch (error) {
      console.error("Error counting pages:", error)
      setPageCountError("Could not determine page count")
    } finally {
      setPageCountLoading(false)
    }
  }

  // Normalize to: lowercase + remove all non-alphanumerics
  // lowercase + strip non-alphanumerics
  const normalize = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")

  // synonyms → canonical keys that match your backend labels
  // "Single Correct Option" => singlecorrectoption
  // "Multiple Correct"      => multiplecorrect
  // "Integer Type"          => integertype
  // "Matrix-Match"          => matrixmatch
  const QUESTION_TYPE_SYNONYMS = {
    mcq: "singlecorrectoption",
    scq: "singlecorrectoption",
    singlecorrect: "singlecorrectoption",
    singlecorrectoption: "singlecorrectoption",
    singlecorrectoptions: "singlecorrectoption",

    msq: "multiplecorrect",
    multiplecorrect: "multiplecorrect",

    integer: "integertype",
    integertype: "integertype",

    matrix: "matrixmatch",
    matrixmatch: "matrixmatch",
  }

  const toCanonical = (s) => QUESTION_TYPE_SYNONYMS[normalize(s)] || normalize(s)
  useEffect(() => {
    if (!showUploadModal || !pendingQTKey) return
    if (!availableQuestionTypes?.length) return

    // availableQuestionTypes are display labels from backend, e.g.:
    // ["Single Correct Option","Multiple Correct","Integer Type","Matrix-Match"]
    const match =
      availableQuestionTypes.find((t) => toCanonical(t) === pendingQTKey) ||
      availableQuestionTypes.find((t) => normalize(t).includes(pendingQTKey)) ||
      availableQuestionTypes[0] ||
      ""

    setUploadForm((prev) => ({ ...prev, questionType: match }))
    setPendingQTKey(null)
  }, [showUploadModal, availableQuestionTypes, pendingQTKey])

  const openUploadForBook = async (book) => {
    const prefill = {
      book: book.book || "",
      class: String(book.class || ""),
      subject: book.subject || "",
      chapter: book.chapter || "",
      examType: book.examType || "",
      questionType: book.questionType || book.originalQuestionType || "",
    }

    setUploadForm(prefill)
    const desired = toCanonical(book.questionType || book.originalQuestionType || "mcq")
    setPendingQTKey(desired)
    setShowUploadModal(true)

    const selectedExam = examTypes.find((e) => e.examName === prefill.examType)
    setAvailableSubjects(selectedExam ? selectedExam.subjects.map((s) => s.name) : [])

    // Load types (await as you already do)
    await fetchQuestionTypes(prefill.examType, prefill.subject)

    // ✅ After availableQuestionTypes is populated, pick the best match
    // availableQuestionTypes should be display labels like "Single correct options"
    const target = toCanonical(prefill.questionType || "mcq")
    const match =
      availableQuestionTypes?.find((t) => toCanonical(t) === target) ||
      availableQuestionTypes?.find((t) => normalize(t).includes(target)) ||
      availableQuestionTypes?.[0] ||
      ""

    setUploadForm((prev) => ({ ...prev, questionType: match }))

    // Reset file + misc (as you already had)
    setSelectedFile(null)
    setFilePageCount(null)
    setPageCountError(null)

    setShowUploadModal(true)
  }

  const handleUploadSubmit = async (e) => {
    e.preventDefault()

    if (
      !selectedFile ||
      !uploadForm.book ||
      !uploadForm.class ||
      !uploadForm.subject ||
      !uploadForm.examType ||
      !uploadForm.questionType
    ) {
      alert("Please fill all required fields, select a file, and choose a question type")
      return
    }

    if (filePageCount > MAX_PAGES) {
      alert(`PDF has too many pages (${filePageCount}). Maximum allowed is ${MAX_PAGES} pages.`)
      return
    }

    try {
      setUploadLoading(true)

      const filename = selectedFile.name
      const uploadResult = await postTeacherUploadFileFOrQuestionBank(selectedFile, filename)
      const s3Uri = uploadResult.uri

      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")

      const requestBody = {
        request: "question-bank-invocationn",
        body: {
          book: uploadForm.book,
          class: uploadForm.class,
          subject: uploadForm.subject,
          chapter: uploadForm.chapter,
          examType: uploadForm.examType,
          questionType: uploadForm.questionType,
          s3uri: s3Uri,
          teacherId: teacherId || "",
          classroomId: classroomId || "",
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/QuestionBank/invocation", "POST", requestBody)

      if (response && response.statusCode === 200) {
        alert("Book uploaded successfully!")
        handleCloseUploadModal()
        fetchAllBooks()
      } else {
        alert("Failed to upload book. Please try again.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      alert("Failed to upload book. Please try again.")
    } finally {
      setUploadLoading(false)
    }
  }

  useEffect(() => {
    if (!localStorage.getItem("teacherId")) {
      localStorage.setItem("teacherId", "teacher123")
    }
    if (!localStorage.getItem("classRoomId")) {
      localStorage.setItem("classRoomId", "classA")
    }
    fetchAllBooks()
    fetchExamTypes()
  }, [])

  const filterOptions = useMemo(() => {
    const examTypes = [...new Set(books.map((book) => book.examType).filter(Boolean))]
    const questionTypes = [
      ...new Set(
        books
          .map((book) => book.questionType) // Changed from book.questionTypes array to book.questionType string
          .filter(Boolean),
      ),
    ]
    const chapters = [...new Set(books.map((book) => book.chapter).filter(Boolean))]

    return {
      examTypes: examTypes.sort(),
      questionTypes: questionTypes.sort(),
      chapters: chapters.sort(),
    }
  }, [books])

  useEffect(() => {
    let filtered = books

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (book) =>
          book.book.toLowerCase().includes(searchTerm.toLowerCase()) ||
          book.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          book.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (book.chapter && book.chapter.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    }

    // Apply exam type filter
    if (filters.examType) {
      filtered = filtered.filter((book) => book.examType === filters.examType)
    }

    // Apply question type filter
    if (filters.questionType) {
      filtered = filtered.filter((book) => book.questionType === filters.questionType)
    }

    // Apply chapter filter
    if (filters.chapter) {
      filtered = filtered.filter((book) => book.chapter === filters.chapter)
    }

    setFilteredBooks(filtered)
  }, [searchTerm, books, filters])

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      examType: "",
      questionType: "",
      chapter: "",
    })
  }

  const activeFiltersCount = Object.values(filters).filter(Boolean).length

  useEffect(() => {
    if (searchTerm) {
      const filtered = books.filter(
        (book) =>
          book.book.toLowerCase().includes(searchTerm.toLowerCase()) ||
          book.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          book.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (book.chapter && book.chapter.toLowerCase().includes(searchTerm.toLowerCase())),
      )
      setFilteredBooks(filtered)
    } else {
      setFilteredBooks(books)
    }
  }, [searchTerm, books])

  const toggleEditQuestionTypeInstructions = (subjectIndex, qtIndex) => {
    const key = `${subjectIndex}-${qtIndex}`
    setExpandedEditQuestionTypes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const renderQuestionTypes = (subject, subjectIndex) => {
    return subject.questionTypes.map((questionType, qtIndex) => (
      <div key={qtIndex} className="bg-white rounded-lg p-4 border border-gray-200 mb-3">
        <div className="flex items-center space-x-3 mb-3">
          <select
            value={questionType.type}
            onChange={(e) => handleEditQuestionTypeChange(subjectIndex, qtIndex, "type", e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            required
          >
            <option value="">Select Question Type</option>
            {questionTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <input
            type="number"
            min="1"
            value={questionType.count}
            onChange={(e) =>
              handleEditQuestionTypeChange(subjectIndex, qtIndex, "count", Number.parseInt(e.target.value) || 1)
            }
            className="w-20 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="Count"
            required
          />

          <button
            type="button"
            onClick={() => toggleEditQuestionTypeInstructions(subjectIndex, qtIndex)}
            className={`px-3 py-2 text-sm rounded transition-colors flex items-center space-x-1 ${
              expandedEditQuestionTypes[`${subjectIndex}-${qtIndex}`]
                ? "bg-indigo-100 text-indigo-700"
                : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
            }`}
            title="Toggle Instructions"
          >
            <FileText className="h-4 w-4" />
            <span>Instructions</span>
            {expandedEditQuestionTypes[`${subjectIndex}-${qtIndex}`] ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {subject.questionTypes.length > 1 && (
            <button
              type="button"
              onClick={() => removeEditQuestionType(subjectIndex, qtIndex)}
              className="text-red-600 hover:text-red-800 p-1"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Added marking scheme fields in a grid layout */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Marks for Correct</label>
            <input
              type="number"
              value={questionType.marksForCorrect || 1}
              onChange={(e) =>
                handleEditQuestionTypeChange(subjectIndex, qtIndex, "marksForCorrect", Number(e.target.value) || 1)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Marks for Incorrect</label>
            <input
              type="number"
              value={questionType.marksForIncorrect ?? ""}
              onChange={(e) => {
                const val = e.target.value
                // allow "-", "", or any valid number
                if (val === "" || val === "-") {
                  handleEditQuestionTypeChange(subjectIndex, qtIndex, "marksForIncorrect", val)
                } else {
                  handleEditQuestionTypeChange(subjectIndex, qtIndex, "marksForIncorrect", Number(val))
                }
              }}
              max={0}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="-1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Marks for Not Answered</label>
            <input
              type="number"
              value={questionType.marksForLeaving || 0}
              onChange={(e) =>
                handleEditQuestionTypeChange(subjectIndex, qtIndex, "marksForLeaving", Number(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="0"
            />
          </div>
        </div>

        {expandedEditQuestionTypes[`${subjectIndex}-${qtIndex}`] && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
            <label className="block text-sm font-medium text-gray-700 mb-2">Instructions for {questionType.type}</label>
            <p className="text-xs text-gray-500 mb-3">
              Provide specific instructions for this question type. These will be shown to students during the exam.
            </p>
            <RichTextEditor
              value={editQuestionTypeInstructions[`${subjectIndex}-${qtIndex}`] || ""}
              onChange={(value) => {
                const key = `${subjectIndex}-${qtIndex}`
                setEditQuestionTypeInstructions((prev) => ({
                  ...prev,
                  [key]: value,
                }))
                handleEditQuestionTypeChange(subjectIndex, qtIndex, "instructions", value)
              }}
              placeholder={`Enter instructions for ${questionType.type} questions...`}
            />
          </div>
        )}
      </div>
    ))
  }
  const handleUploadAnswerKeyClick = () => {
    setShowUploadAnswerKeyModal(true)
  }
  // Heuristic image-URI extractor
  // Heuristic image-URI extractor (PDFs filtered out)
  const extractImageUris = (obj) => {
    const seen = new Set()
    const allowedExt = /\.(png|jpe?g|gif|webp|svg|bmp|tiff?)$/i
    const looksLikeImagePath = /\/(images?|img|media)\//i

    const isImg = (s) =>
      typeof s === "string" &&
      (/^data:image\//i.test(s) ||
        // s3://... only if ends with image ext or path hints images
        (/^s3:\/\//i.test(s) && (allowedExt.test(s) || looksLikeImagePath.test(s))) ||
        // http(s)://... image ext or images/ path
        (/^https?:\/\//i.test(s) && (allowedExt.test(s) || looksLikeImagePath.test(s))))

    const walk = (v) => {
      if (!v) return
      if (typeof v === "string") {
        if (isImg(v)) seen.add(v)
        return
      }
      if (Array.isArray(v)) return v.forEach(walk)
      if (typeof v === "object") for (const val of Object.values(v)) walk(val)
    }

    walk(obj)
    return Array.from(seen)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search books..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  showFilters || activeFiltersCount > 0
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-white text-blue-600 text-xs px-2 py-1 rounded-full">{activeFiltersCount}</span>
                )}
              </button>

              <button
                onClick={handleUploadAnswerKeyClick}
                className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
              >
                <UploadIcon className="h-4 w-4" />
                <span>Upload Answer Key</span>
              </button>
              <button
                onClick={handleViewExamTypesClick}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                <Book className="h-4 w-4" />
                <span>View Exam Types</span>
              </button>
              <button
                onClick={handleExamTypeClick}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
              >
                <Settings className="h-4 w-4" />
                <span>Add Exam Type</span>
              </button>
              <button
                onClick={handleUploadClick}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                <UploadIcon className="h-4 w-4" />
                <span>Upload Book</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="border-t border-gray-200 py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Exam Type:</label>
                  <select
                    value={filters.examType}
                    onChange={(e) => handleFilterChange("examType", e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Exam Types</option>
                    {filterOptions.examTypes.map((examType) => (
                      <option key={examType} value={examType}>
                        {examType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Question Type:</label>
                  <select
                    value={filters.questionType}
                    onChange={(e) => handleFilterChange("questionType", e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Question Types</option>
                    {filterOptions.questionTypes.map((questionType) => (
                      <option key={questionType} value={questionType}>
                        {questionType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Chapter:</label>
                  <select
                    value={filters.chapter}
                    onChange={(e) => handleFilterChange("chapter", e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Chapters</option>
                    {filterOptions.chapters.map((chapter) => (
                      <option key={chapter} value={chapter}>
                        {chapter}
                      </option>
                    ))}
                  </select>
                </div>

                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                    <span>Clear Filters</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Your Books</h2>
              <span className="text-sm text-gray-500">
                {filteredBooks.length} of {books.length} books
              </span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span>Loading books...</span>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button onClick={fetchAllBooks} className="text-blue-600 hover:text-blue-800">
                Try again
              </button>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">
                {books.length === 0 ? "No books found." : "No books match your current filters."}
              </p>
              {activeFiltersCount > 0 && (
                <button onClick={clearFilters} className="text-blue-600 hover:text-blue-800">
                  Clear filters to see all books
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Chapter
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class–Subject
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Exam Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status & Progress
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBooks.map((book) => {
                    const successPercentage = calculateSuccessPercentage(book.successfulChunks, book.totalChunks)
                    const daysSinceCreation = calculateDaysSinceCreation(book.createdAt)

                    return (
                      <tr key={book._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            <span>{book.chapter || "N/A"}</span>
                            {duplicateLabelMap[book._id]?.label > 0 && (
                              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                                {duplicateLabelMap[book._id].label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {`${String(book.class)}-${(book.subject || "").toLowerCase()}`}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {(book.questionType || book.originalQuestionType || "N/A").toLowerCase()}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{book.examType || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(book.status)}`}
                            >
                              {book.status}
                            </span>
                            {book.totalChunks ? (
                              <div className="flex items-center">
                                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      successPercentage == 100
                                        ? "bg-green-500"
                                        : successPercentage >= 50
                                          ? "bg-yellow-500"
                                          : "bg-red-500"
                                    }`}
                                    style={{ width: `${successPercentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-900">{successPercentage}%</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">No data</span>
                            )}
                            {book.answerKeyUploaded && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                                Answer key uploaded
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                // 1) prefer server-provided images on each book
                                const fromServer = Array.isArray(book.images) ? book.images : []

                                // 2) fallback: scan the whole book object (filters .pdf)
                                const fromDeep = extractImageUris(book)

                                // 3) merge + de-dupe
                                const merged = Array.from(new Set([...fromServer, ...fromDeep]))

                                console.log(
                                  "[images][UploadAndEditor→Edit] Found",
                                  merged.length,
                                  "image URIs:",
                                  merged,
                                )

                                setSelectedBookImageUris(merged)
                                setSelectedBookId(book._id)
                                setShowBookDetailsModal(true)
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View
                            </button>

                            <button
                              onClick={() => {
                                setCopyTargetBook(book)
                                setCopyExamType("") // reset dropdown selection
                                setShowCopyModal(true)
                              }}
                              className="text-emerald-600 hover:text-emerald-900"
                              title="Copy questions to another exam type"
                            >
                              <div className="h-8 w-8 mx-auto mb-4 mt-3 flex items-center justify-center rounded-full bg-emerald-100 hover:bg-emerald-200 transition-colors">
                                <Copy className="h-6 w-6 text-emerald-600" />
                              </div>
                            </button>
                            <button
                              onClick={() => openUploadForBook(book)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Upload another PDF for this chapter"
                            >
                              <div className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-200 transition-colors">
                                <Plus className="h-5 w-5" />
                              </div>
                            </button>

                            <button
                              onClick={() => showDeleteConfirmationDialog(book)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Book"
                            >
                              <div className="h-8 w-8 mx-auto mb-3 mt-3 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 transition-colors">
                                <Trash className="h-6 w-6 text-red-600" />
                              </div>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Upload New Book</h2>
                <p className="text-gray-600 mt-1">Add educational content to your collection</p>
              </div>
              <button
                onClick={handleCloseUploadModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex" style={{ height: "calc(95vh - 120px)" }}>
              {/* Left Panel - Form */}
              <div className="w-1/2 border-r bg-gray-50/30 flex flex-col">
                <div className="flex-1 p-8 overflow-y-auto">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Book Name *</label>
                      <input
                        type="text"
                        name="book"
                        value={uploadForm.book}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter book name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Class *</label>
                      <input
                        type="text"
                        name="class"
                        value={uploadForm.class}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="e.g., 12, 10, etc."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Exam Type *</label>
                      <select
                        name="examType"
                        value={uploadForm.examType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        required
                        disabled={examTypesLoading}
                      >
                        <option value="">{examTypesLoading ? "Loading exam types..." : "Select Exam Type"}</option>
                        {examTypes.map((examType) => (
                          <option key={examType._id} value={examType.examName}>
                            {examType.examName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subject *</label>
                      <select
                        name="subject"
                        value={uploadForm.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        required
                        disabled={!uploadForm.examType || availableSubjects.length === 0}
                      >
                        <option value="">
                          {!uploadForm.examType
                            ? "Select exam type first"
                            : availableSubjects.length === 0
                              ? "No subjects available"
                              : "Select Subject"}
                        </option>
                        {availableSubjects.map((subject) => (
                          <option key={subject} value={subject}>
                            {subject}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Question Type Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
                      <select
                        value={uploadForm.questionType}
                        onChange={(e) => {
                          const newQuestionType = e.target.value
                          setUploadForm({ ...uploadForm, questionType: newQuestionType })

                          // Auto-populate default instructions and fetch existing ones
                          if (newQuestionType && uploadForm.examType && uploadForm.subject) {
                            const key = `${uploadForm.examType}-${uploadForm.subject}-${newQuestionType}`
                            // Set default instructions first
                            setQuestionTypeInstructions((prev) => ({
                              ...prev,
                              [key]: defaultQuestionTypeInstructions[newQuestionType] || "",
                            }))
                            // Then fetch any existing custom instructions
                            fetchQuestionTypeInstructions(uploadForm.examType, uploadForm.subject, newQuestionType)
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select Question Type</option>
                        {questionTypesLoading ? (
                          <option disabled>Loading question types...</option>
                        ) : (
                          availableQuestionTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Chapter *</label>
                      <input
                        type="text"
                        name="chapter"
                        value={uploadForm.chapter}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder=" chapter name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">PDF File *</label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t bg-white">
                  <form onSubmit={handleUploadSubmit}>
                    <button
                      type="submit"
                      disabled={uploadLoading || (filePageCount && filePageCount > MAX_PAGES)}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed flex items-center justify-center font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      {uploadLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mr-2" />
                          Upload Book
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Right Panel - PDF Preview */}
              <div className="w-1/2 p-8 bg-gradient-to-br from-gray-50 to-blue-50 overflow-y-auto">
                <h3 className="text-xl font-bold text-gray-900 mb-6">PDF Preview</h3>

                {selectedFile ? (
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <FileText className="h-8 w-8 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    </div>

                    {pageCountLoading ? (
                      <div className="flex items-center justify-center p-6 bg-white rounded-xl border border-gray-200">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600 font-medium">Analyzing PDF...</span>
                      </div>
                    ) : filePageCount ? (
                      <div
                        className={`p-4 rounded-xl border ${
                          filePageCount > MAX_PAGES ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"
                        }`}
                      >
                        <div className="flex items-center">
                          {filePageCount > MAX_PAGES ? (
                            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
                          ) : (
                            <FileText className="h-6 w-6 text-emerald-500 mr-3" />
                          )}
                          <div>
                            <span
                              className={`text-base font-semibold ${
                                filePageCount > MAX_PAGES ? "text-red-700" : "text-emerald-700"
                              }`}
                            >
                              {filePageCount} pages detected
                            </span>
                            {filePageCount > MAX_PAGES && (
                              <p className="text-sm text-red-600 mt-1">Exceeds maximum limit of {MAX_PAGES} pages</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {pageCountError && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center">
                          <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
                          <span className="text-sm text-red-700 font-medium">{pageCountError}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-80 border-2 border-dashed border-gray-300 rounded-xl bg-white/50">
                    <div className="text-center">
                      <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-500 mb-2">No file selected</p>
                      <p className="text-sm text-gray-400">Choose a PDF file to see preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showViewExamTypesModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">View Exam Types</h2>
                <p className="text-gray-600 mt-1">Manage your exam type configurations</p>
              </div>
              <button
                onClick={handleCloseViewExamTypesModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(95vh - 120px)" }}>
              {examTypesLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-green-600 mr-2" />
                  <span>Loading exam types...</span>
                </div>
              ) : examTypes.length === 0 ? (
                <div className="p-8 text-center">
                  <Settings className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No exam types found</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {examTypes.map((examType) => (
                    <div key={examType._id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{examType.examName}</h3>
                        <button
                          onClick={() => handleEditExamType(examType)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </button>
                      </div>

                      {examType.examInstructions && (
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-2">Instructions:</h4>
                          <div
                            className="text-sm text-gray-700"
                            dangerouslySetInnerHTML={{ __html: examType.examInstructions }}
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {examType.subjects.map((subject, subjectIndex) => (
                          <div key={subjectIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-2">{subject.name}</h4>
                            <p className="text-sm text-gray-600 mb-1">
                              Total Questions: <span className="font-medium">{subject.totalQuestions}</span>
                            </p>
                            <p className="text-sm text-gray-600 mb-3">
                              Time: <span className="font-medium">{subject.timeInMinutes} minutes</span>
                            </p>

                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-700">Question Types:</p>
                              {subject.questionTypes.map((questionType, qtIndex) => (
                                <div
                                  key={qtIndex}
                                  className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded"
                                >
                                  <span className="text-sm text-gray-700">{questionType.type}</span>
                                  <span className="text-sm font-medium text-gray-900">{questionType.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 text-sm text-gray-500">
                        Created: {formatDate(examType.createdAt)} | Updated: {formatDate(examType.updatedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditExamTypeModal && selectedExamTypeForEdit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Edit Exam Type</h2>
                <p className="text-gray-600 mt-1">Modify exam type configuration</p>
              </div>
              <button
                onClick={handleCloseEditExamTypeModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(95vh - 180px)" }}>
              <form onSubmit={handleEditSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Exam Name *</label>
                  <input
                    type="text"
                    value={editExamTypeForm.examName}
                    onChange={(e) => handleEditFormChange("examName", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter exam name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Exam Instructions</label>
                  <p className="text-sm text-gray-500 mb-3">
                    Provide detailed instructions for students taking this exam
                  </p>
                  <RichTextEditor
                    value={editExamTypeForm.examInstructions}
                    onChange={(value) => handleEditFormChange("examInstructions", value)}
                    placeholder="Enter exam instructions with formatting..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Subjects</h3>
                    <button
                      type="button"
                      onClick={addEditSubject}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Add Subject
                    </button>
                  </div>

                  {editExamTypeForm.subjects.map((subject, subjectIndex) => (
                    <div key={subjectIndex} className="bg-gray-50 rounded-xl p-6 mb-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold text-gray-900">Subject {subjectIndex + 1}</h4>
                        {editExamTypeForm.subjects.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEditSubject(subjectIndex)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Subject Name *</label>
                          <input
                            type="text"
                            value={subject.name}
                            onChange={(e) => handleEditSubjectChange(subjectIndex, "name", e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="e.g., Physics, Chemistry"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Total Questions *</label>
                          <input
                            type="number"
                            min="1"
                            value={subject.totalQuestions}
                            onChange={(e) =>
                              handleEditSubjectChange(
                                subjectIndex,
                                "totalQuestions",
                                Number.parseInt(e.target.value) || 1,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Time (Minutes) *</label>
                          <input
                            type="number"
                            min="1"
                            value={subject.timeInMinutes}
                            onChange={(e) =>
                              handleEditSubjectChange(
                                subjectIndex,
                                "timeInMinutes",
                                Number.parseInt(e.target.value) || 60,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">Question Types</label>
                          <button
                            type="button"
                            onClick={() => addEditQuestionType(subjectIndex)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Add Type
                          </button>
                        </div>

                        {renderQuestionTypes(subject, subjectIndex)}
                      </div>
                    </div>
                  ))}
                </div>
              </form>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseEditExamTypeModal}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {editLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  "Update Exam Type"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Delete Book</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{bookToDelete?.book}"? This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteBook(bookToDelete._id)}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCopyModal && copyTargetBook && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-emerald-50 to-green-50">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Copy Questions</h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {copyTargetBook.book} — {copyTargetBook.chapter || "Untitled Chapter"}
                </p>
              </div>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close copy modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select target exam type</label>
                <select
                  value={copyExamType}
                  onChange={(e) => setCopyExamType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Choose exam type</option>
                  {examTypes.map((et) => (
                    <option key={et._id} value={et.examName}>
                      {et.examName}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Current exam type: <span className="font-medium">{copyTargetBook.examType || "N/A"}</span>
                </p>
              </div>

              {!!copyExamType && copyExamType === copyTargetBook.examType && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  Target exam type must be different from the current one.
                </div>
              )}
            </div>

            <div className="p-5 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCopyModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                disabled={copyLoading}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!copyExamType) {
                    alert("Please select an exam type")
                    return
                  }
                  if (copyExamType === copyTargetBook.examType) {
                    alert("New exam type must be different from the current exam type")
                    return
                  }

                  try {
                    setCopyLoading(true)
                    // Call your /invocation API with request: "copy-questions-to-exam"
                    const resp = await postAws("https://api-dev.aurait.ai/QuestionBank/invocation", "POST", {
                      request: "copy-questions-to-exam",
                      body: {
                        invocationId: copyTargetBook._id,
                        examType: copyExamType,
                      },
                    })

                    if (resp && resp.statusCode === 200) {
                      // Show loader while we refresh, then close
                      await fetchAllBooks() // refresh grid
                      setShowCopyModal(false)
                      setCopyTargetBook(null)
                      setCopyExamType("")
                      alert("Copied successfully!")
                    } else {
                      alert(resp?.body?.message || "Copy failed. Please try again.")
                    }
                  } catch (e) {
                    console.error("Copy failed:", e)
                    alert("Copy failed. Please try again.")
                  } finally {
                    setCopyLoading(false)
                  }
                }}
                disabled={copyLoading || !copyExamType || copyExamType === copyTargetBook.examType}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {copyLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Copying...
                  </>
                ) : (
                  "Copy"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exam Type Modal */}
      {showExamTypeModal && <AddQuestionAddExamType showModal={showExamTypeModal} onClose={handleCloseExamTypeModal} />}

      {/* Book Details Modal */}
      {showBookDetailsModal && (
        <AddQuestionQuestionbankEditExtractedQuetsions
          bookId={selectedBookId}
          showModal={showBookDetailsModal}
          onClose={handleCloseBookDetailsModal}
          initialImageUris={selectedBookImageUris}
        />
      )}

      {showUploadAnswerKeyModal && <AddQuestionUploadAnswerkey onClose={() => setShowUploadAnswerKeyModal(false)} />}
    </div>
  )
}

export default AddQuestionQuestionBankUploadAndEditor
